import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EXPENSE_GROUPS = [
  "Alimentación",
  "Tarjetas",
  "Servicios Fijos",
  "Operativos",
  "Estilo de Vida",
  "Salud/Educación",
  "Servicios Profesionales",
  "Otros",
];

const COLORS = ["#0052FF", "#EF4444", "#FBBF24", "#00E590", "#CBD5E1", "#3B82F6", "#DC2626", "#94A3B8"];

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("expense");
  const [newGroup, setNewGroup] = useState("Otros");
  const [newColor, setNewColor] = useState("#0052FF");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error("Ingresa un nombre");
      return;
    }
    setCreating(true);
    try {
      await api.post("/categories", {
        name: newName.trim(),
        type: newType,
        group: newType === "income" ? "Ingresos" : newGroup,
        color: newColor,
      });
      toast.success("Categoría creada");
      setNewName("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail != null ? formatApiErrorDetail(detail) : (err?.message || "Error al crear");
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("[Categories] create failed", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Categoría eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const income = categories.filter((c) => c.type === "income");
  const expense = categories.filter((c) => c.type === "expense");

  const grouped = expense.reduce((acc, c) => {
    const g = c.group || "Otros";
    if (!acc[g]) acc[g] = [];
    acc[g].push(c);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="mb-8 fade-up">
        <p className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] font-medium mb-2">
          Configuración
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#F1F5F9]">
          Categorías
        </h1>
      </div>

      {/* Create new */}
      <form onSubmit={handleCreate} className="clay-card p-5 mb-8 fade-up" data-testid="new-category-form">
        <h2 className="font-display text-lg font-semibold text-[#F1F5F9] mb-4">
          Crear categoría personalizada
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Nombre</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ej. Streaming"
              className="mt-2 bg-[#131C2E] border-[#2A3447]"
              data-testid="cat-name-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Tipo</Label>
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger className="mt-2 bg-[#131C2E] border-[#2A3447]" data-testid="cat-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E]">
                <SelectItem value="expense">Gasto</SelectItem>
                <SelectItem value="income">Ingreso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {newType === "expense" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Grupo</Label>
              <Select value={newGroup} onValueChange={setNewGroup}>
                <SelectTrigger className="mt-2 bg-[#131C2E] border-[#2A3447]" data-testid="cat-group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131C2E]">
                  {EXPENSE_GROUPS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={newType === "expense" ? "" : "md:col-span-2"}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Color</Label>
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={`w-7 h-7 rounded-full transition-all ${
                    newColor === c ? "ring-2 ring-offset-2 ring-[#0F172A]" : ""
                  }`}
                  style={{ backgroundColor: c }}
                  data-testid={`color-swatch-${c}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            type="submit"
            disabled={creating}
            className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
            data-testid="create-category-button"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <Plus className="w-4 h-4 mr-2" /> Agregar
              </>
            )}
          </Button>
        </div>
      </form>

      <Tabs defaultValue="expense" className="fade-up">
        <TabsList className="bg-[#1B2638]">
          <TabsTrigger value="expense" data-testid="tab-cat-expense" className="data-[state=active]:bg-[#131C2E]">
            Gastos ({expense.length})
          </TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-cat-income" className="data-[state=active]:bg-[#131C2E]">
            Ingresos ({income.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expense" className="mt-6 space-y-6">
          {loading ? (
            <p className="text-sm text-[#94A3B8]">Cargando...</p>
          ) : (
            Object.entries(grouped).map(([group, cats]) => (
              <div key={group} className="clay-card p-5">
                <h3 className="font-display text-sm uppercase tracking-[0.2em] text-[#94A3B8] mb-4">
                  {group}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {cats.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 px-3 py-2 bg-[#131C2E] border border-[#2A3447] hover:border-[#3B4A66] rounded-md transition-colors"
                      data-testid={`cat-pill-${c.id}`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full ring-1 ring-white/10"
                        style={{ backgroundColor: c.color }}
                      />
                      <span className="text-sm font-medium text-[#F8FAFC]">{c.name}</span>
                      {!c.is_default && (
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="text-[#94A3B8] hover:text-[#EF4444] transition-colors"
                          data-testid={`delete-cat-${c.id}`}
                          aria-label={`Eliminar ${c.name}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="income" className="mt-6">
          <div className="clay-card p-5">
            <div className="flex flex-wrap gap-2">
              {income.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-2 px-3 py-2 bg-[#131C2E] border border-[#2A3447] hover:border-[#3B4A66] rounded-md transition-colors"
                  data-testid={`cat-pill-${c.id}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full ring-1 ring-white/10"
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="text-sm font-medium text-[#F8FAFC]">{c.name}</span>
                  {!c.is_default && (
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="text-[#94A3B8] hover:text-[#EF4444] transition-colors"
                      data-testid={`delete-cat-${c.id}`}
                      aria-label={`Eliminar ${c.name}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
